import type { AppSettingsRepository } from '@/app/settings/app-settings';

export const FIELD_AUTO_SYNC_INTERVAL_MS = 60_000;
export const FIELD_AUTO_SYNC_STATUSES = {
  disabled: 'disabled',
  failed: 'failed',
  idle: 'idle',
  running: 'running',
  synced: 'synced',
} as const;

export type FieldAutoSyncStatus = (typeof FIELD_AUTO_SYNC_STATUSES)[keyof typeof FIELD_AUTO_SYNC_STATUSES];

export interface FieldSyncRunnerLike {
  runAll: (now?: string) => Promise<unknown>;
}

interface WindowLike {
  addEventListener: (type: 'online', listener: () => void) => void;
  removeEventListener: (type: 'online', listener: () => void) => void;
}

interface NavigatorLike {
  onLine?: boolean;
}

export interface FieldAutoSyncControllerInput {
  clearInterval?: (timerId: unknown) => void;
  createRunner: () => Promise<FieldSyncRunnerLike> | FieldSyncRunnerLike;
  intervalMs?: number;
  navigatorLike?: NavigatorLike;
  now?: () => string;
  onStatus?: (status: FieldAutoSyncStatus) => void;
  setInterval?: (listener: () => void, intervalMs: number) => unknown;
  settingsRepository: AppSettingsRepository;
  windowLike: WindowLike;
}

export class FieldAutoSyncController {
  private intervalId?: unknown;
  private isRunning = false;
  private removeListener?: () => void;

  constructor(private readonly input: FieldAutoSyncControllerInput) {}

  start() {
    const listener = () => {
      void this.checkAndRun();
    };
    this.input.windowLike.addEventListener('online', listener);
    this.removeListener = () => this.input.windowLike.removeEventListener('online', listener);
    this.intervalId = this.createInterval(listener);
    void this.checkAndRun();

    return () => this.stop();
  }

  stop() {
    this.removeListener?.();
    this.removeListener = undefined;
    if (this.intervalId) {
      this.clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  async checkAndRun() {
    if (this.isRunning) {
      return;
    }

    const settings = await this.input.settingsRepository.load();

    if (!settings.autoSyncEnabled) {
      this.input.onStatus?.(FIELD_AUTO_SYNC_STATUSES.disabled);
      return;
    }

    if (this.input.navigatorLike?.onLine === false) {
      this.input.onStatus?.(FIELD_AUTO_SYNC_STATUSES.idle);
      return;
    }

    this.isRunning = true;
    this.input.onStatus?.(FIELD_AUTO_SYNC_STATUSES.running);

    try {
      const runner = await this.input.createRunner();
      await runner.runAll(this.input.now?.() ?? new Date().toISOString());
      this.input.onStatus?.(FIELD_AUTO_SYNC_STATUSES.synced);
    } catch {
      this.input.onStatus?.(FIELD_AUTO_SYNC_STATUSES.failed);
    } finally {
      this.isRunning = false;
    }
  }

  private createInterval(listener: () => void) {
    const setIntervalFn = this.input.setInterval ?? globalThis.setInterval?.bind(globalThis);

    return setIntervalFn?.(listener, this.input.intervalMs ?? FIELD_AUTO_SYNC_INTERVAL_MS);
  }

  private clearInterval(timerId: unknown) {
    const clearIntervalFn = this.input.clearInterval ?? globalThis.clearInterval?.bind(globalThis);

    clearIntervalFn?.(timerId as never);
  }
}
